import Tweenr from 'tweenr';
import { debounce } from 'lodash-es';
import GestureHelper from './GestureHelper';
import ContextMenuBlocker from './ContextMenuBlocker';
import ViewportZoomControl from './ViewportZoomControl';
import { clamp } from './math';
import { parseElementHtml } from './html';
import { EventHandler, EventHandlerList } from '../../utils/dom-events';


function makeElement(baseClassName) {
  return parseElementHtml(`
    <div class="${baseClassName}">
      <div class="${baseClassName}__transform-origin-marker"></div>
      <!-- zoom control -->
      <div class="${baseClassName}__content-wrapper">
        <!-- passed in content goes here -->
      </div>
      <div class="${baseClassName}__margin-display" style="display: none;"></div>
    </div>
  `);
}

const MARGIN = 25;
const DEFAULT_MIN_SCALE = 0.1;
const DOUBLE_CLICK_INCREMENT = 0.44;

// iOS runs out of memory and crashes with large 3D transforms:
const use3DTransform = /iPhone|iPad|iPod/.test(window.navigator.userAgent) === false;


class Viewport {
  constructor(opts = {}) {
    const options = Object.assign({
      baseClassName: 'viewport',
      drawTransformOrigin: false,
      drawMargins: false,
      onClick: null,
      zoomControl: {},
    }, opts);

    this.bindMethods();

    this.drawTransformOrigin = options.drawTransformOrigin;
    this.scale = 1;
    this.minScale = DEFAULT_MIN_SCALE;
    this.previousScale = this.scale;

    this.transformOrigin = { x: 0, y: 0 };

    this.el = makeElement(options.baseClassName);

    // Firefox long press via touch triggers context menu
    this.contextMenuBlocker = new ContextMenuBlocker(this.el);

    this.tweenr = new Tweenr();
    this.containerEl = this.el.querySelector(`.${options.baseClassName}__content-wrapper`);
    this.originMarkerEl = this.el.querySelector(`.${options.baseClassName}__transform-origin-marker`);
    this.el.appendChild(this.containerEl);

    if (this.drawTransformOrigin === false) {
      this.originMarkerEl.style.display = 'none';
    }

    if (options.drawMargins) {
      const marginDisplayEl = this.el.querySelector(`.${options.baseClassName}__margin-display`);
      marginDisplayEl.style.display = 'block';
      marginDisplayEl.style.borderWidth = `${MARGIN}px`;
    }

    this.gestures = new GestureHelper(this.containerEl, {
      onPanMove: this.onPanMove,
      onPress: e => e.preventDefault(),
      onPinch: this.onPinch,
      onDoubleClick: this.onDoubleClick,
      onRelease: this.onRelease,
      passive: false,
      // debugId: 'Viewport',
    });

    this.zoomControl = new ViewportZoomControl(Object.assign({}, options.zoomControl, {
      onUpdate: this.onZoomUpdate,
      onReset: this.fitContents,
    }));
    this.el.appendChild(this.zoomControl.el);

    this.resizeHandlers = new EventHandlerList([
      new EventHandler(window, 'resize', debounce(this.onWindowResize, 20), { passive: true }),
      new EventHandler(window, 'scroll', debounce(this.updateContainerRect, 20), { passive: true }),
    ]).start();
  }

  startListening() {
    this.gestures.start();
    this.zoomControl.startListening();
  }
  stopListening() {
    this.gestures.stop();
    this.zoomControl.stopListening();
  }

  bindMethods() {
    this.updateContainerRect = this.updateContainerRect.bind(this);
    this.onPanMove = this.onPanMove.bind(this);
    this.onPinch = this.onPinch.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onRelease = this.onRelease.bind(this);
    this.onTweenComplete = this.onTweenComplete.bind(this);
    this.onZoomUpdate = this.onZoomUpdate.bind(this);
    this.fitContents = this.fitContents.bind(this);
    this.render = this.render.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  setContentEl(el, width = 0, height = 0) {
    // const rect = el.getBoundingClientRect();

    this.contentRect = {
      x: 0,
      y: 0,
      width,
      height,
    };

    this.contentEl = el;
    this.containerEl.appendChild(this.contentEl);

    this.updateContainerRect();

    this.fitContents(false);
  }

  calcInitialScale() {
    const { contentRect, containerRect } = this;

    const doubleMargin = MARGIN * 2;

    const fittingWidthPerc = (containerRect.width - doubleMargin) / contentRect.width;
    const fittingHeightPerc = (containerRect.height - doubleMargin) / contentRect.height;
    const fittingScale = Math.min(fittingWidthPerc, fittingHeightPerc);

    const minScale = Math.min(fittingScale, DEFAULT_MIN_SCALE);

    const scale = fittingScale;

    this.minScale = minScale;
    return scale;
  }

  updateContainerRect() {
    const rect = this.containerEl.getBoundingClientRect();

    this.containerRect = {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
      half: {
        width: rect.width * 0.5,
        height: rect.height * 0.5,
      },
    };
  }

  onWindowResize() {
    this.updateContainerRect();
    this.fitContents(false);
  }

  centerContents() {
    const { containerRect } = this;
    const scaledDimensions = this.getScaledDimensions(this.scale);

    this.contentRect.x = Math.round((containerRect.width - scaledDimensions.width) * 0.5);
    this.contentRect.y = Math.round((containerRect.height - scaledDimensions.height) * 0.5);
  }

  onPanMove(event, coords) {
    event.preventDefault();

    this.contentRect.x += coords.offset.x;
    this.contentRect.y += coords.offset.y;

    this.render();
  }

  onRelease(event, coords) {
    event.preventDefault();

    if (coords.didPinch && coords.didPan) {
      const minMaxedScale = clamp(this.scale, this.minScale, 1);
      if (this.scale !== minMaxedScale) {
        this.animateToScale(minMaxedScale, this.transformOrigin, 0.3, 'backOut');
      }
    }
  }

  restrainCoordinates() {
    const { contentRect, containerRect } = this;
    const bounds = this.getScaledDimensions(this.scale);

    const xMax = containerRect.half.width;
    const xMin = containerRect.half.width - bounds.width;
    const yMax = containerRect.half.height;
    const yMin = containerRect.half.height - bounds.height;

    const tooFarUp = contentRect.y < yMin;
    const tooFarDown = contentRect.y > yMax;
    const tooFarLeft = contentRect.x < xMin;
    const tooFarRight = contentRect.x > xMax;

    // console.log({ tooFarUp, tooFarDown, tooFarLeft, tooFarRight });

    if (tooFarRight) {
      contentRect.x = xMax;
    } else if (tooFarLeft) {
      contentRect.x = xMin;
    }

    if (tooFarDown) {
      contentRect.y = yMax;
    } else if (tooFarUp) {
      contentRect.y = yMin;
    }
  }

  onPinch(event, coords) {
    event.preventDefault();

    this.contentRect.x += coords.offset.x;
    this.contentRect.y += coords.offset.y;

    this.updateTransformOrigin({
      x: coords.current.x - this.containerRect.x,
      y: coords.current.y - this.containerRect.y,
    });

    const currentDistance = coords.pinch.distance.current;
    const previousDistance = coords.pinch.distance.previous;
    let difference = currentDistance / previousDistance;

    // console.table({ currentDistance, previousDistance, difference });

    if (this.scale === 0 && difference > 1) {
      // this is not great :\
      this.scale = 0.2;
      difference = 1;
    }

    this.adjustScaleByPercent(difference);

    this.render();
  }

  onDoubleClick(event, coords) {
    event.preventDefault();

    const center = {
      x: coords.current.x - this.containerRect.x,
      y: coords.current.y - this.containerRect.y,
    };

    const mod = event.shiftKey ? -1 : 1;

    this.animateToScale(this.scale + (DOUBLE_CLICK_INCREMENT * mod), center);
  }

  getScaledDimensions(scale) {
    return {
      width: this.contentRect.width * scale,
      height: this.contentRect.height * scale,
    };
  }

  render() {
    this.restrainCoordinates();

    // NO HEAVY LIFTING IN HERE!
    window.requestAnimationFrame(() => {
      if (this.drawTransformOrigin) {
        this.originMarkerEl.style.left = `${this.transformOrigin.x}px`;
        this.originMarkerEl.style.top = `${this.transformOrigin.y}px`;
      }

      const { x, y } = this.contentRect;

      let transform = `translate(${x}px, ${y}px) scale(${this.scale})`;

      if (use3DTransform) {
        transform += ' translateZ(0)';
      }

      this.contentEl.style.transform = transform;
    });
  }

  animateToScale(value = 1, centerPoint = null, duration = 0.3, ease = 'quadOut') {
    const scale = clamp(value, this.minScale, 1);

    if (scale === this.scale) {
      return;
    }

    this.updateTransformOrigin(centerPoint);

    const startingScale = this.scale;
    const endingScale = scale;

    const tweeningData = {
      scale: startingScale,
    };

    this.startTween(tweeningData, { scale: endingScale, ease, duration }, () => {
      let changeInPercent;
      if (this.scale === 0) {
        this.scale = tweeningData.scale;
        changeInPercent = 1;
      } else {
        changeInPercent = tweeningData.scale / this.scale;
      }

      this.adjustScaleByPercent(changeInPercent);
      this.render();
    });
  }

  adjustScaleByPercent(adjustment) {
    this.scale = Math.max(0, this.scale * adjustment);

    const { contentRect, transformOrigin } = this;

    const dimensions = this.getScaledDimensions(this.scale);
    const previousDimensions = this.getScaledDimensions(this.previousScale);


    const originPercentX = (transformOrigin.x - contentRect.x) / previousDimensions.width;
    const originPercentY = (transformOrigin.y - contentRect.y) / previousDimensions.height;

    const zoomAdjustmentX = (previousDimensions.width - dimensions.width) * originPercentX;
    const zoomAdjustmentY = (previousDimensions.height - dimensions.height) * originPercentY;

    contentRect.x += zoomAdjustmentX;
    contentRect.y += zoomAdjustmentY;

    this.previousScale = this.scale;

    this.updateZoomControl();
  }

  onTweenComplete() {
    this.tween = null;
    this.contentRect.x = Math.round(this.contentRect.x);
    this.contentRect.y = Math.round(this.contentRect.y);

    this.updateZoomControl();
    this.render();
  }

  onZoomUpdate(value) {
    this.animateToScale(this.convertToRenderingScale(value));
  }

  fitContents(animate = true) {
    const scale = this.calcInitialScale();

    const { containerRect, contentRect } = this;
    const scaledDimensions = this.getScaledDimensions(scale);

    const x = Math.round((containerRect.width - scaledDimensions.width) * 0.5);
    const y = Math.round((containerRect.height - scaledDimensions.height) * 0.5);

    if (animate) {
      const tweeningData = {
        x: contentRect.x,
        y: contentRect.y,
        scale: this.scale,
      };

      this.startTween(tweeningData, { x, y, scale, ease: 'cubicInOut', duration: 0.33 }, () => {
        this.previousScale = this.scale;
        this.scale = tweeningData.scale;
        this.contentRect.x = tweeningData.x;
        this.contentRect.y = tweeningData.y;

        this.updateZoomControl();
        this.render();
      });
    } else {
      this.previousScale = scale;
      this.scale = scale;
      this.contentRect.x = x;
      this.contentRect.y = y;

      this.updateZoomControl();
      this.render();
    }
  }

  startTween(from, to, onUpdate) {
    if (this.tween) {
      this.tween.cancel();
    }

    this.tween = this.tweenr.to(from, to)
      .on('update', onUpdate)
      .on('complete', this.onTweenComplete)
      .on('cancelling', this.onTweenComplete);
  }

  detach() {
    this.el.parentNode.removeChild(this.el);
  }

  destroy() {
    // TODO double-check we've got everything here
    this.tweenr.dispose();

    this.contextMenuBlocker.destroy();

    if (this.tween) {
      this.tween.cancel();
      this.tween = null;
    }

    this.gestures.destroy();
    this.gestures = null;

    this.zoomControl.destroy();
    this.zoomControl = null;

    this.resizeHandlers.stop();
    this.resizeHandlers = null;
  }

  updateTransformOrigin(centerPoint = null) {
    if (centerPoint === null) {
      this.transformOrigin.x = this.containerRect.half.width;
      this.transformOrigin.y = this.containerRect.half.height;
    } else {
      this.transformOrigin.x = centerPoint.x;
      this.transformOrigin.y = centerPoint.y;
    }
  }

  convertToRenderingScale(normalScale) {
    return (normalScale * (1 - this.minScale)) + this.minScale;
  }

  convertFromRenderingScale(renderingScale = this.scale) {
    return (renderingScale - this.minScale) / (1 - this.minScale);
  }

  updateZoomControl() {
    if (this.zoomControl) {
      const perc = this.convertFromRenderingScale();
      this.zoomControl.setValue(perc);
    }
  }
}

export default Viewport;



// WEBPACK FOOTER //
// ./V7/syos/utils/Viewport.js